import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl, FormattedMessage } from 'react-intl';
import { Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import * as brandApi from 'apis/restaurant-api/merchant-admin/brand/api';
import * as enterpriseApi from 'apis/restaurant-api/merchant-admin/enterprise/api';
import * as merchantApi from 'apis/restaurant-api/merchant-admin/merchant/api';
import * as venueApi from 'apis/restaurant-api/merchant-admin/venue/api';
import { useExternalAccessRights, useRetailContext } from 'common/hooks';
import { usePermissions } from 'common/wolt-permissions';
import {
  hasApiKeyGenerationPermission,
  hasMerchantAppCreateDeletePermissions,
  hasMerchantAppLoginPermissions,
} from 'common/wolt-permissions/permissionUtils';
import { useCanViewUserManagement, MessagePage } from 'modules/experience-core';
import { PageHeader, useExperienceEntityVenues } from 'modules/experience-core';
import { useExperienceModuleContext } from 'modules/experience-core';
import { ExperienceEntityType } from 'modules/experience-core';

import AccessManagementNavigation from './AccessManagementNavigation';
import ApiManagement from './ApiManagement';
import AppPasswords from './AppPasswords';
import DirectAddModal from './DirectAddModal';
import InviteModal from './InviteModal';
import UserManagement from './UserManagement';
import { reportAddExistingUser, reportInviteUser, reportRemoveAccess } from './behavioral';
import SftpIntegrationManagement from './integration-sftp/SftpIntegrationManagement';
import { AccessDescription } from './types';
import { parseApiError } from './utils';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const FetchMappings: Record<ExperienceEntityType, Function> = {
  [ExperienceEntityType.MERCHANT]: merchantApi.fetchMerchantAccess,
  [ExperienceEntityType.VENUE]: venueApi.fetchVenueAccess,
  [ExperienceEntityType.BRAND]: brandApi.fetchBrandAccess,
  [ExperienceEntityType.ENTERPRISE]: enterpriseApi.fetchEnterpriseAccess,
};

const RemoveAccessMappings: Record<ExperienceEntityType, Function> = {
  [ExperienceEntityType.MERCHANT]: merchantApi.deleteMerchantAccess,
  [ExperienceEntityType.VENUE]: venueApi.deleteVenueAccess,
  [ExperienceEntityType.BRAND]: brandApi.deleteBrandAccess,
  [ExperienceEntityType.ENTERPRISE]: enterpriseApi.deleteEnterpriseAccess,
};

export const ExperienceUserManagement = () => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const intl = useIntl();
  const { selectedExperienceEntity } = useExperienceModuleContext();

  const { experienceEntityId, experienceEntityType, experienceEntityObject } = selectedExperienceEntity;
  const entityVenues = useExperienceEntityVenues().map(
    experienceVenueEntity => experienceVenueEntity.experienceEntityObject,
  );

  const isRetailContext = useRetailContext();
  const accessRights = useExternalAccessRights();

  const fetchFunc = FetchMappings[experienceEntityType];
  const deleteFunc = RemoveAccessMappings[experienceEntityType];

  const {
    data: roles = null,
    error,
    refetch,
  } = useQuery<AccessDescription[], AxiosError>(
    [`/v1/merchant-admin/${experienceEntityType}s/${experienceEntityId}/access`],
    () => fetchFunc(experienceEntityId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const removeAccessMutation = useMutation(
    (user: AccessDescription) => deleteFunc(experienceEntityId, user.email, user.id),
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error: AxiosError) => {
        toast.error(<FormattedMessage id={parseApiError(error)} />, { duration: Infinity });
      },
    },
  );

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const permissions = usePermissions();

  const handleInviteSuccess = () => {
    setIsInviteModalOpen(false);
    refetch();
    reportInviteUser('merchant');
  };

  const handleDirectAddSuccess = () => {
    setIsAddUserModalOpen(false);
    refetch();
    reportAddExistingUser('merchant');
  };

  const handleRemoveAccess = async (user: AccessDescription) => {
    await removeAccessMutation.mutateAsync(user);
    reportRemoveAccess('merchant');
  };

  useEffect(() => {
    if (experienceEntityType) {
      history.replace(`${url}/user-management`);
    }
  }, [experienceEntityType, history, url]);

  let navigationItems = null;
  if (experienceEntityType === ExperienceEntityType.MERCHANT) {
    navigationItems = [
      ...(hasApiKeyGenerationPermission(permissions)
        ? [
            {
              href: `${url}/api-access`,
              name: intl.formatMessage({ id: 'merchant-integrations.menu-api-access-header' }),
            },
          ]
        : []),
      ...(hasApiKeyGenerationPermission(permissions)
        ? [
            {
              href: `${url}/sftp-integration`,
              name: intl.formatMessage({ id: 'merchant-integrations.sftp.route-name' }),
            },
          ]
        : []),
    ];
  }

  if (experienceEntityType === ExperienceEntityType.VENUE) {
    navigationItems = [
      ...(hasMerchantAppLoginPermissions(permissions, accessRights, experienceEntityObject)
        ? [
            {
              href: `${url}/app-passwords`,
              name: intl.formatMessage({ id: 'userManagement.appPasswords' }),
            },
          ]
        : []),
    ];
  }

  const canViewUserManagement = useCanViewUserManagement(selectedExperienceEntity);

  return canViewUserManagement ? (
    <Root>
      <PageHeader />
      {['venue', 'merchant'].includes(experienceEntityType) && (
        <AccessManagementNavigation
          items={[
            {
              href: `${url}/user-management`,
              name: intl.formatMessage({ id: 'navigation.user-management' }),
            },
            ...(navigationItems || []),
          ]}
        />
      )}
      <Switch>
        <Redirect from={url} exact to={`${url}/user-management`} />
        <Route path={`${url}/user-management`}>
          <UserManagement
            entityId={experienceEntityId}
            entityType={experienceEntityType}
            errorReason={error ? parseApiError(error) : null}
            isRemovingAccess={removeAccessMutation.isLoading}
            isRetailContext={isRetailContext}
            onAddUser={() => setIsAddUserModalOpen(true)}
            onInviteUser={() => setIsInviteModalOpen(true)}
            onRemoveAccess={handleRemoveAccess}
            roles={roles}
          />
        </Route>
        {experienceEntityType === ExperienceEntityType.VENUE && (
          <Route path={`${url}/app-passwords`}>
            <AppPasswords
              venueId={experienceEntityId}
              canCreateDelete={hasMerchantAppCreateDeletePermissions(permissions, accessRights, experienceEntityObject)}
            />
          </Route>
        )}
        {hasApiKeyGenerationPermission(permissions) && (
          <>
            <Route path={`${url}/api-access`}>
              <ApiManagement merchantId={experienceEntityId} merchantVenues={entityVenues} />
            </Route>
            <Route path={`${url}/sftp-integration`}>
              <SftpIntegrationManagement merchantId={experienceEntityId} merchantVenues={entityVenues} />
            </Route>
          </>
        )}
      </Switch>
      <InviteModal
        entityId={experienceEntityId}
        entityType={experienceEntityType}
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />
      <DirectAddModal
        entityId={experienceEntityId}
        entityType={experienceEntityType}
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onSuccess={handleDirectAddSuccess}
      />
    </Root>
  ) : (
    <MessagePage variant="missing_permissions" />
  );
};
